<template>
  <div class="dashboard">
    <!-- Sidebar -->
    <div :class="['sidebar', { 'sidebar-collapsed': !isSidebarExpanded }]">
      <Sidebar @toggle="handleSidebarToggle" />
    </div>

    <!-- Main Content -->
    <div
      :class="['main-content', { 'main-content-expanded': isSidebarExpanded }]"
    >
      <!-- Header -->
      <header>
        <div>
          <WaitingDriversNumber :waiting-captains="waitingCaptains" />
        </div>
        <div class="header-icons">
          <i class="fas fa-plus-circle"></i>
        </div>
      </header>

      <!-- Tabs -->
      <div class="tabs">
        <button
          :class="{ 'active-tab': activeTab === 'all' }"
          @click="setActiveTab('all')"
        >
          All <span class="count-badge">{{ allCount }}</span>
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'pending' }"
          @click="setActiveTab('pending')"
        >
          PENDING <span class="count-badge">{{ pendingCount }}</span>
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'end' }"
          @click="setActiveTab('end')"
        >
          COMPLETED
          <span class="count-badge">{{ completedCount }}</span>
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'cancelledByUser' }"
          @click="setActiveTab('cancelledByUser')"
        >
          CANCELLED
          <span class="count-badge">{{ cancelledByUserCount }}</span>
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'cancelledByCaptain' }"
          @click="setActiveTab('cancelledByCaptain')"
        >
          CANCELLED BY CAPTAIN
          <span class="count-badge">{{ cancelledByCaptainCount }}</span>
        </button>
      </div>

      <!-- Table Controls -->
      <div class="table-controls">
        <input
          type="text"
          v-model="searchUniqueId"
          placeholder="Search by Trip ID"
        />
        <select v-model="vehicleTypeFilter">
          <option value="">All Vehicles</option>
          <option value="car">Car</option>
          <option value="motorcycle">Motorcycle</option>
          <option value="van">Van</option>
        </select>
        <input type="date" v-model="dateFilter" placeholder="Filter by Date" />
      </div>

      <!-- Table -->
      <div class="table-container">
        <div v-if="loading" class="loading">Loading trips...</div>
        <div v-else-if="paginatedTrips.length === 0" class="no-data">
          No trips available
        </div>
        <table v-else>
          <thead>
            <tr>
              <th>User Name</th>
              <th>User Phone</th>
              <th>Driver Name</th>
              <th>Driver Phone</th>
              <th>Vehicle</th>
              <th>Trip ID</th>
              <th>Ordered Time</th>
              <th>Start Location</th>
              <th>Finish Location</th>
              <th>Value</th>
              <th>Payment</th>
              <th>Wallet user after trip</th>
              <th>Wallet driver after trip</th>
              <th>Trip Comment</th>
            </tr>
          </thead>
          <tbody>
            <!-- row clickable -->
            <tr
              v-for="trip in paginatedTrips"
              :key="trip._id"
              class="trip-row"
              @click="openTripDetails(trip)"
            >
              <!-- User Name -->
              <td>
                <div style="display: flex; align-items: center">
                  <span>{{ trip.userId?.username || "N/A" }}</span>
                </div>
              </td>

              <!-- User Phone -->
              <td>{{ trip.userId?.phoneNumber || "N/A" }}</td>

              <!-- Driver Name -->
              <td>
                <div style="display: flex; align-items: center">
                  <img
                    v-if="trip.driverId?.profile_image"
                    :src="trip.driverId?.profile_image"
                    alt="Driver Image"
                    style="
                      width: 40px;
                      height: 40px;
                      border-radius: 50%;
                      object-fit: cover;
                      margin-right: 8px;
                    "
                  />
                  <span>{{ trip.driverId?.username || "N/A" }}</span>
                </div>
              </td>

              <!-- Driver Phone -->
              <td>{{ trip.driverId?.phoneNumber || "N/A" }}</td>

              <!-- Vehicle -->
              <td>
                <span v-if="trip.driverId">
                  {{ trip.vehicleType || "N/A" }}
                  ({{ trip.driverId?.username || "N/A" }})
                </span>
                <span v-else>
                  {{ trip.vehicleType || "N/A" }}
                </span>
              </td>

              <!-- Trip ID (الرقم الطويل) -->
              <td>{{ trip._id || "N/A" }}</td>

              <!-- Ordered Time -->
              <td>{{ formatDate(trip.createdAt) }}</td>

              <!-- Start Location -->
              <td>{{ trip.pickupLocationName || "N/A" }}</td>

              <!-- Finish Location -->
              <td>{{ trip.destination || "N/A" }}</td>

              <!-- Value -->
              <td>{{ formatCurrency(trip.cost || 0) }} EGP</td>

              <!-- Payment -->
              <td>
                <template v-if="trip.userMoneyFlowId?.flow?.[0]">
                  {{ formatCurrency(trip.userMoneyFlowId.flow[0].payWallet) }}
                  Wallet +
                  {{ formatCurrency(trip.userMoneyFlowId.flow[0].payCash) }}
                  Cash EGP
                  <br />
                  <small
                    >Total:
                    {{
                      formatCurrency(
                        (trip.userMoneyFlowId.flow[0].payWallet || 0) +
                          (trip.userMoneyFlowId.flow[0].payCash || 0)
                      )
                    }}
                    EGP
                  </small>
                </template>
                <span v-else>N/A</span>
              </td>

              <!-- Wallet user after trip -->
              <td>
                {{
                  formatCurrency(
                    trip.userMoneyFlowId?.flow?.[0]?.walletAfter || 0
                  )
                }}
                EGP
              </td>

              <!-- Wallet driver after trip -->
              <td>
                <span
                  :class="{
                    'wallet-non-zero':
                      trip.driverMoneyFlowId?.flow?.[0]?.walletAfter !== 0,
                    'wallet-zero':
                      !trip.driverMoneyFlowId?.flow?.[0]?.walletAfter,
                  }"
                >
                  {{
                    formatCurrency(
                      trip.driverMoneyFlowId?.flow?.[0]?.walletAfter || 0
                    )
                  }}
                  EGP
                </span>
              </td>

              <!-- Trip Comment -->
              <td>{{ trip.comment || "N/A" }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination" v-if="paginatedTrips.length > 0">
          <span>
            {{ (currentPage - 1) * itemsPerPage + 1 }}-{{
              Math.min(currentPage * itemsPerPage, filteredTrips.length)
            }}
            of {{ filteredTrips.length }} items
          </span>
          <button :disabled="currentPage === 1" @click="currentPage--">
            prev
          </button>
          <button disabled>{{ currentPage }}</button>
          <button :disabled="currentPage >= totalPages" @click="currentPage++">
            next
          </button>
        </div>
      </div>

      <!-- Modal: Trip Details -->
      <div
        v-if="showDetailsModal"
        class="modal-overlay"
        @click.self="closeTripDetails"
      >
        <div class="modal">
          <div class="modal-header">
            <h3>
              Trip Details
              <span v-if="tripData"> ({{ tripData._id }}) </span>
            </h3>
            <button class="close-button" @click="closeTripDetails">×</button>
          </div>

          <div class="modal-body">
            <div v-if="detailsLoading" class="loading">Loading details...</div>
            <div v-else-if="detailsError" class="error">
              {{ detailsError }}
            </div>

            <div v-else-if="tripData">
              <!-- Header: User + Trip basic info -->
              <div class="trip-head">
                <div class="user-info">
                  <div class="avatar" v-if="tripData.userId?.profile_image">
                    <img :src="tripData.userId.profile_image" alt="user" />
                  </div>
                  <div class="avatar placeholder" v-else>
                    {{
                      initials(tripData.userId?.username || tripData.username)
                    }}
                  </div>
                  <div>
                    <div class="username">
                      {{
                        tripData.userId?.username ||
                        tripData.username ||
                        "Unknown User"
                      }}
                    </div>
                    <div class="trip-id">Trip ID: {{ tripData._id }}</div>
                    <div class="user-phone">
                      User Phone:
                      {{ tripData.userId?.phoneNumber || "N/A" }}
                    </div>
                  </div>
                </div>

                <div class="badges">
                  <span class="badge" :class="statusClass(tripData.status)">
                    {{ tripData.status || "unknown" }}
                  </span>
                  <span class="badge warn" v-if="tripData.isFake">
                    Fake Trip
                  </span>
                  <span
                    class="badge"
                    v-else
                    style="background: #dcfce7; color: #166534"
                  >
                    Real Trip
                  </span>
                </div>
              </div>

              <!-- Route -->
              <div class="route-card">
                <div class="route-row">
                  <span class="dot from"></span>
                  <div class="route-text">
                    <div class="label">From</div>
                    <div class="value">
                      {{
                        tripData.pickupLocationName ||
                        tripData.locationName ||
                        "—"
                      }}
                    </div>
                  </div>
                </div>
                <div class="route-line"></div>
                <div class="route-row">
                  <span class="dot to"></span>
                  <div class="route-text">
                    <div class="label">To</div>
                    <div class="value">
                      {{ tripData.destination || "—" }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Metrics -->
              <div class="metrics-grid">
                <div class="metric">
                  <div class="metric-label">Distance</div>
                  <div class="metric-value">
                    {{ formatKm(tripData.distance) }}
                  </div>
                </div>
                <div class="metric">
                  <div class="metric-label">Duration</div>
                  <div class="metric-value">
                    {{ formatMinutes(tripData.duration || tripData.time) }}
                  </div>
                </div>
                <div class="metric">
                  <div class="metric-label">Cost</div>
                  <div class="metric-value">
                    {{ formatCurrency(tripData.cost) }} EGP
                  </div>
                </div>
                <div class="metric">
                  <div class="metric-label">Vehicle</div>
                  <div class="metric-value">
                    {{ tripData.vehicleType || "—" }}
                  </div>
                </div>
                <div class="metric">
                  <div class="metric-label">Payment</div>
                  <div class="metric-value">
                    {{ tripData.paymentType || "N/A" }}
                  </div>
                </div>
                <div class="metric">
                  <div class="metric-label">Date</div>
                  <div class="metric-value">
                    {{ tripData.date || formatDateTime(tripData.createdAt) }}
                  </div>
                </div>
              </div>

              <!-- Seen By Captains -->
              <div class="section" v-if="seenByUnique.length">
                <div class="section-title">
                  Seen by captains ({{ seenByUnique.length }})
                </div>
                <div class="avatar-grid">
                  <div
                    class="avatar-item"
                    v-for="s in seenByUnique"
                    :key="s.driverId"
                  >
                    <img :src="s.imageUrl" alt="captain" />
                    <button
                      class="avatar-caption link-like"
                      @click.stop="goToCaptain(s.driverId)"
                    >
                      ID: {{ s.driverId }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Offers -->
              <div class="section">
                <div class="section-title">
                  Offers ({{ offersList.length }})
                </div>

                <div v-if="offersList.length === 0" class="muted">
                  No offers
                </div>

                <table v-else class="offers-table">
                  <thead>
                    <tr>
                      <th>Captain</th>
                      <th>Captain Phone</th>
                      <th>Vehicle</th>
                      <th>Offer Price</th>
                      <th>Distance from User</th>
                      <th>ETA</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="o in offersList" :key="o._id">
                      <td>
                        <div class="offer-captain">
                          <img
                            v-if="o.driverId?.profile_image"
                            :src="o.driverId.profile_image"
                            alt=""
                          />
                          <div class="avatar tiny" v-else>
                            {{ initials(o.driverId?.username) }}
                          </div>
                          <span
                            class="captain-link"
                            @click.stop="goToCaptain(o.driverId)"
                          >
                            {{ o.driverId?.username || "—" }}
                          </span>
                        </div>
                      </td>
                      <td>
                        {{ o.driverId?.phoneNumber || "N/A" }}
                      </td>
                      <td>{{ o.driverId?.vehicleType || "—" }}</td>
                      <td>
                        <span class="price">
                          {{ formatCurrency(o.offer) }} EGP
                        </span>
                      </td>
                      <td>{{ formatMeters(o.distance) }}</td>
                      <td>{{ formatMinutes(o.time) }}</td>
                      <td>{{ formatDateTime(o.createdAt) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Optional raw JSON -->
              <details class="raw-json">
                <summary>Show raw JSON</summary>
                <pre class="json-view">{{ formattedTripDetails }}</pre>
              </details>
            </div>
          </div>
        </div>
      </div>
      <!-- End Modal -->
    </div>
  </div>
</template>

<script>
import Sidebar from "./sidebarComponent.vue";
import axios from "@/axios";
import WaitingDriversNumber from "@/components/waitingDriversNumber.vue";

export default {
  name: "tripsComponent",
  components: {
    WaitingDriversNumber,
    Sidebar,
  },
  data() {
    return {
      activeTab: "all",
      trips: [],
      cancelledByUserTrips: [],
      cancelledByCaptainTrips: [],
      isSidebarExpanded: true,
      baseUrl: "https://backend.fego-rides.com",
      loading: false,
      searchUniqueId: "",
      vehicleTypeFilter: "",
      dateFilter: "",
      currentPage: 1,
      itemsPerPage: 10,
      adminUsername: "",
      waitingCaptains: 0,

      // modal state
      showDetailsModal: false,
      selectedTrip: null,
      selectedTripDetails: null,
      detailsLoading: false,
      detailsError: "",
    };
  },
  computed: {
    filteredTrips() {
      let filtered = [];

      if (this.activeTab === "cancelledByUser") {
        filtered = this.cancelledByUserTrips;
      } else if (this.activeTab === "cancelledByCaptain") {
        filtered = this.cancelledByCaptainTrips;
      } else {
        filtered = this.trips;

        if (this.activeTab !== "all") {
          filtered = filtered.filter((trip) => trip.status === this.activeTab);
        }
      }

      if (this.searchUniqueId) {
        const query = this.searchUniqueId.toLowerCase();
        filtered = filtered.filter((trip) => {
          const id = trip._id || trip.uniqueId || "";
          return id.toLowerCase().includes(query);
        });
      }

      if (this.vehicleTypeFilter) {
        filtered = filtered.filter(
          (trip) => trip.vehicleType === this.vehicleTypeFilter
        );
      }

      if (this.dateFilter) {
        filtered = filtered.filter((trip) => {
          const tripDate = new Date(trip.createdAt).toISOString().split("T")[0];
          return tripDate === this.dateFilter;
        });
      }

      return filtered;
    },
    paginatedTrips() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTrips.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredTrips.length / this.itemsPerPage);
    },
    allCount() {
      return this.trips.length;
    },
    pendingCount() {
      return this.trips.filter((trip) => trip.status === "pending").length;
    },
    completedCount() {
      return this.trips.filter((trip) => trip.status === "end").length;
    },
    cancelledByUserCount() {
      return this.cancelledByUserTrips.length;
    },
    cancelledByCaptainCount() {
      return this.cancelledByCaptainTrips.length;
    },

    // من ريسبونس tripDetails
    tripData() {
      return this.selectedTripDetails?.trip || null;
    },
    offersList() {
      return this.selectedTripDetails?.offers || [];
    },
    seenByUnique() {
      const arr = this.selectedTripDetails?.trip?.seenBy || [];
      const seen = new Map();
      for (const s of arr) {
        if (s?.driverId && s?.imageUrl && !seen.has(s.driverId)) {
          seen.set(s.driverId, s);
        }
      }
      return Array.from(seen.values());
    },
    formattedTripDetails() {
      if (!this.selectedTripDetails) return "";
      return JSON.stringify(this.selectedTripDetails, null, 2);
    },
  },
  watch: {
    searchUniqueId() {
      this.currentPage = 1;
    },
    vehicleTypeFilter() {
      this.currentPage = 1;
    },
    dateFilter() {
      this.currentPage = 1;
    },
    activeTab() {
      this.currentPage = 1;
    },
  },
  created() {
    this.adminUsername = localStorage.getItem("username");
    this.getTrips();
    this.getCancelledByUserTrips();
    this.getCancelledByCaptainTrips();
  },
  methods: {
    handleSidebarToggle() {
      this.isSidebarExpanded = !this.isSidebarExpanded;
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      if (tab === "cancelledByUser") {
        this.getCancelledByUserTrips();
      } else if (tab === "cancelledByCaptain") {
        this.getCancelledByCaptainTrips();
      }
    },

    // list APIs
    async getTrips() {
      try {
        this.loading = true;
        const response = await axios.post(`${this.baseUrl}/wallet/get-data`);
        this.trips = response.data.trips || [];
        console.log("Trips:", this.trips);
      } catch (error) {
        console.error("Error fetching trips:", error);
      } finally {
        this.loading = false;
      }
    },
    async getCancelledByUserTrips() {
      try {
        this.loading = true;
        const response = await axios.get(
          `${this.baseUrl}/admin/get-cancelled-trips`
        );
        this.cancelledByUserTrips = response.data.trips || [];
        console.log("Cancelled by User Trips:", this.cancelledByUserTrips);
      } catch (error) {
        console.error("Error fetching cancelled by user trips:", error);
      } finally {
        this.loading = false;
      }
    },
    async getCancelledByCaptainTrips() {
      try {
        this.loading = true;
        const response = await axios.get(
          `${this.baseUrl}/admin/get-cancelled-by-driver`
        );
        this.cancelledByCaptainTrips = response.data.trips || [];
        console.log(
          "Cancelled by Captain Trips:",
          this.cancelledByCaptainTrips
        );
      } catch (error) {
        console.error("Error fetching cancelled by captain trips:", error);
      } finally {
        this.loading = false;
      }
    },

    // trip details
    async openTripDetails(trip) {
      this.selectedTrip = trip;
      this.showDetailsModal = true;
      this.detailsLoading = true;
      this.detailsError = "";
      this.selectedTripDetails = null;

      try {
        const response = await axios.get(
          `${this.baseUrl}/book/tripDetails/${trip._id}`
        );
        this.selectedTripDetails = response.data;
        console.log("Trip details:", this.selectedTripDetails);
      } catch (error) {
        console.error("Error fetching trip details:", error);
        this.detailsError = "Error fetching trip details";
      } finally {
        this.detailsLoading = false;
      }
    },
    closeTripDetails() {
      this.showDetailsModal = false;
      this.selectedTrip = null;
      this.selectedTripDetails = null;
      this.detailsError = "";
    },

    // go to captain details page
    goToCaptain(driverData) {
      // نطلّع الـ id من أي فورمات يجيلنا
      let id = null;

      if (typeof driverData === "string") {
        id = driverData;
      } else if (driverData && typeof driverData === "object") {
        id = driverData._id || driverData.driverId || driverData.id || null;
      }

      if (!id) {
        console.warn("goToCaptain: no valid driver id", driverData);
        return;
      }

      this.$router.push({
        name: "DriverDetails",
        params: { driverId: id },
      });
    },

    // helpers
    initials(name) {
      if (!name) return "U";
      const parts = String(name).trim().split(" ").filter(Boolean);
      return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
    },
    statusClass(status) {
      switch ((status || "").toLowerCase()) {
        case "pending":
          return "badge-pending";
        case "end":
          return "badge-success";
        case "cancelled":
        case "cancelledbyuser":
        case "cancelledbycaptain":
          return "badge-cancel";
        default:
          return "badge-muted";
      }
    },
    formatKm(val) {
      if (val === null || val === undefined || val === "") return "—";
      const num = Number(val);
      if (Number.isNaN(num)) return String(val);
      return `${num.toFixed(2)} km`;
    },
    formatMeters(val) {
      if (!val && val !== 0) return "—";
      const m = Number(val);
      if (Number.isNaN(m)) return String(val);
      return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m} m`;
    },
    formatMinutes(val) {
      if (!val && val !== 0) return "—";
      const n = Number(val);
      if (Number.isNaN(n)) return String(val);
      return `${n} min`;
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    },
    formatCurrency(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
/* Base Styles */
.dashboard {
  display: flex;
  min-height: 100vh;
  font-family: "Arial", sans-serif;
  justify-content: space-between;
}

/* Sidebar */
.sidebar {
  width: 250px;
  transition: width 0.3s ease;
}

.sidebar-collapsed {
  width: 80px;
}

/* Main Content */
.main-content {
  background-color: #ffffff;
  padding: 24px;
  transition: margin-left 0.3s ease;
}

.main-content-expanded {
  margin-left: 250px;
}

.main-content-expanded.sidebar-collapsed {
  margin-left: 80px;
}

/* Header */
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #1f2a44;
}

header p {
  font-size: 14px;
  color: #2563eb;
  cursor: pointer;
  margin-top: 4px;
}

.header-icons i {
  font-size: 20px;
  color: #6b7280;
}

/* Tabs */
.tabs {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  margin-top: 24px;
}

.tabs button {
  flex: 1;
  padding-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  text-align: center;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tabs button.active-tab {
  color: #000000;
  border-bottom: 2px solid #2563eb;
}

.count-badge {
  display: inline-block;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  background-color: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  margin-left: 8px;
  padding: 0 6px;
  text-align: center;
}

/* Table Controls */
.table-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.table-controls input,
.table-controls select {
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 14px;
}

/* Table */
.table-container {
  margin-top: 24px;
  background-color: #ffffff;
  border-radius: 8px;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 12px;
  text-align: left;
}

th {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  background-color: #f9fafb;
  text-transform: uppercase;
}

td {
  font-size: 14px;
  color: #374151;
  border-top: 1px solid #e5e7eb;
}

/* hover row */
.trip-row {
  cursor: pointer;
}
.trip-row:hover {
  background-color: #f3f4f6;
}

.wallet-non-zero {
  display: inline-block;
  padding: 4px 8px;
  background-color: #d1fae5;
  color: #059669;
  border-radius: 9999px;
  font-size: 14px;
}

.wallet-zero {
  display: inline-block;
  padding: 4px 8px;
  background-color: #f3f4f6;
  color: #4b5563;
  border-radius: 9999px;
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
}

.pagination span {
  font-size: 14px;
  color: #6b7280;
}

.pagination button {
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: #ffffff;
  cursor: pointer;
  font-size: 14px;
}

.pagination button:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

/* Loading and No Data States */
.loading,
.no-data {
  text-align: center;
  padding: 24px;
  font-size: 16px;
  color: #6b7280;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 60px;
  z-index: 9999;
}

.modal {
  background-color: #ffffff;
  border-radius: 8px;
  width: 80%;
  max-width: 900px;
  max-height: 80vh;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
}

.modal-body {
  padding: 12px 16px 16px;
  overflow: auto;
}

.json-view {
  background-color: #f9fafb;
  padding: 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
  max-height: 65vh;
  overflow: auto;
}

.error {
  color: #b91c1c;
  font-size: 14px;
}

/* Trip details UI */
.trip-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar,
.avatar.placeholder {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #eef2ff;
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  overflow: hidden;
  text-transform: uppercase;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.username {
  font-weight: 600;
}

.trip-id {
  font-size: 12px;
  color: #6b7280;
}

.user-phone {
  font-size: 12px;
  color: #4b5563;
}

.badges {
  display: flex;
  gap: 8px;
}

.badge {
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background: #f3f4f6;
  color: #374151;
}

.badge-success {
  background: #dcfce7;
  color: #166534;
}

.badge-pending {
  background: #fef9c3;
  color: #854d0e;
}

.badge-cancel {
  background: #fee2e2;
  color: #991b1b;
}

.badge-muted {
  background: #f3f4f6;
  color: #374151;
}

.badge.warn {
  background: #fee2e2;
  color: #991b1b;
}

/* Route card */
.route-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.route-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.route-line {
  height: 10px;
  border-left: 2px dashed #cbd5e1;
  margin: 6px 0 6px 7px;
}

.dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
}

.dot.from {
  background: #22c55e;
}

.dot.to {
  background: #3b82f6;
}

.route-text .label {
  font-size: 12px;
  color: #6b7280;
}

.route-text .value {
  font-size: 14px;
  font-weight: 500;
}

/* Metrics */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.metric {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
}

.metric-label {
  font-size: 12px;
  color: #6b7280;
}

.metric-value {
  font-size: 16px;
  font-weight: 600;
}

/* Sections */
.section {
  margin-top: 14px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 8px;
}

/* Seen captains */
.avatar-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.avatar-item img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.avatar-caption {
  font-size: 11px;
  color: #4b5563;
  margin-top: 2px;
}

.link-like {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

/* Offers */
.offers-table {
  width: 100%;
  border-collapse: collapse;
}

.offers-table th,
.offers-table td {
  padding: 10px;
  border-top: 1px solid #e5e7eb;
  text-align: left;
}

.offer-captain {
  display: flex;
  align-items: center;
  gap: 8px;
}

.offer-captain img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar.tiny {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

.captain-link {
  cursor: pointer;
  text-decoration: underline;
}

.price {
  font-weight: 700;
}

.muted {
  color: #6b7280;
}

.raw-json {
  margin-top: 10px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 16px;
  }

  .sidebar {
    width: 80px;
  }

  .main-content-expanded {
    margin-left: 80px;
  }

  header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-icons {
    margin-top: 8px;
  }

  .tabs {
    flex-wrap: wrap;
  }

  .tabs button {
    flex: 1 1 50%;
    margin-bottom: 8px;
  }

  .table-controls {
    flex-direction: column;
    gap: 8px;
  }

  .modal {
    width: 95%;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.main-content {
  width: 90%;
}

@media (max-width: 1920px) {
  .main-content {
    width: 85%;
  }
}
</style>
